<script lang="ts">
	import type { ClassValue, HTMLAttributes } from 'svelte/elements';
	import BtnBase from './btnBase.svelte';
	import type { BtnSize, BtnTheme } from './types';
	import type { Snippet } from 'svelte';

	const sizeClasses: Record<BtnSize, string> = {
		small: 'py-3 px-4 text-[1rem]/[110%]',
		'small-mobile': 'py-[0.75rem] px-4 text-[1rem]/[110%] lg:py-4 lg:px-6 lg:text-[18px]/[140%]',
		medium: 'py-4 px-6  text-[18px]/[110%]',
		regular: 'py-4 px-6 text-[1rem]/[120%]',
		'regular-mobile': 'py-5 px-10 text-[1rem]/[120%] lg:text-[20px]/[100%]',
		nav: 'px-7 py-3 lg:py-4 lg:px-10 text-[1rem] max-lg:tracking-[-.16px] lg:text-[1rem]/[110%]',
		large: 'py-4 px-6 text-[1.125rem]/[120%]'
	};

	const themeClasses: Record<BtnTheme, { btn: string; fill: string }> = {
		default: {
			btn: 'bg-green-3 text-stone-5 active:max-lg:duration-[0ms] active:max-lg:bg-green-1 active:max-lg:text-stone-5',
			fill: 'bg-green-1'
		},
		outline: {
			btn: 'border border-brown-1',
			fill: ''
		},
		light: {
			btn: 'bg-green-3 text-stone-5 active:max-lg:duration-[0ms] active:max-lg:bg-green-2 active:max-lg:text-stone-5',
			fill: 'bg-green-2'
		},
		dark: { btn: 'bg-green-2 text-green-5', fill: 'bg-green-1' },
		stone: {
			btn: 'bg-stone-5 text-green-3 transition-colors duration-[350ms] lg:hover:text-stone-5',
			fill: 'bg-green-3'
		},
		'stone-green-border': {
			btn: 'bg-stone-5 text-green-3 border-2 border-green-3 transition-colors duration-[350ms] lg:hover:text-stone-5',
			fill: 'bg-green-3'
		},
		'transparent-green-border': {
			btn: 'text-green-3 border-2 border-green-3 transition-colors duration-[350ms] lg:hover:text-stone-5 active:max-lg:duration-[0ms] active:max-lg:bg-green-3 active:max-lg:text-stone-5',
			fill: 'bg-green-3'
		},
		'transparent-stone-border': {
			btn: 'text-stone-5 border-2 border-stone-5 transition-colors duration-[350ms] lg:hover:text-green-3',
			fill: 'bg-stone-5'
		},
		'transparent-brown-border': {
			btn: 'text-brown-1 border-2 border-brown-1 transition-colors duration-[350ms] lg:hover:text-stone-5 active:max-lg:duration-[0ms] active:max-lg:bg-brown-1 active:max-lg:text-stone-5',
			fill: 'bg-brown-1'
		},
		lime: {
			btn: 'bg-green-5 text-green-1 transition-colors duration-[350ms] lg:hover:text-green-5',
			fill: 'bg-green-1'
		},
		'lime-brown': {
			btn: 'bg-green-5 text-brown-1',
			fill: ''
		},
		'lime-light': {
			btn: 'bg-green-5 text-green-1 transition-colors duration-[350ms] lg:hover:text-stone-5',
			fill: 'bg-green-3'
		},
		'nyc-green': { btn: 'bg-green-4 text-[#001A10]', fill: 'bg-[#6FC28B]' },
		white: { btn: 'bg-white text-brown-1', fill: 'bg-[#E9E9E2]' },
		'brown-white': { btn: 'bg-brown-1 text-white', fill: 'bg-black' }
	};

	interface Props extends HTMLAttributes<HTMLAnchorElement | HTMLButtonElement> {
		btn?: HTMLAnchorElement | HTMLButtonElement;
		size?: BtnSize;
		theme?: BtnTheme;
		hasIcon?: boolean;
		children?: Snippet;
		class?: ClassValue;
		disabled?: boolean;
		href?: HTMLAnchorElement['href'];
		rel?: HTMLAnchorElement['rel'];
		target?: HTMLAnchorElement['target'];
		type?: HTMLButtonElement['type'];
		contentClass?: string;
	}

	let {
		btn = $bindable(),
		size = 'regular',
		theme = 'default',
		hasIcon = false,
		children,
		class: className,
		disabled = false,
		...other
	}: Props = $props();

	let sizeClass = $derived(sizeClasses[size]);
	let themeClass = $derived(themeClasses[theme]);
	let iconClasses = $derived(hasIcon ? 'font-semibold' : '');
</script>

<BtnBase
	{...other}
	{disabled}
	bind:btn
	class={[
		'rounded-full text-center',
		sizeClass,
		themeClass.btn,
		iconClasses,
		...(Array.isArray(className) ? className : [className])
	]}
	fillClass={themeClass.fill}>{@render children?.()}</BtnBase
>
